import { onBeforeUnmount, watch, type WatchSource } from 'vue'
import { absoluteUrl, DEFAULT_OG_IMAGE, SITE_NAME } from '@/seo/site'
import type { SeoHeadInput } from '@/seo/types'

function upsertMetaByName(name: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute('name', name)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function upsertMetaByProperty(property: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute('property', property)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function upsertCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', 'canonical')
    document.head.appendChild(element)
  }
  element.setAttribute('href', href)
}

function upsertJsonLd(data: SeoHeadInput['jsonLd']) {
  const existing = document.head.querySelectorAll<HTMLScriptElement>('script[data-seo-jsonld="true"]')
  existing.forEach((node) => node.remove())

  if (!data) {
    return
  }

  const payloads = Array.isArray(data) ? data : [data]
  for (const payload of payloads) {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.dataset.seoJsonld = 'true'
    script.textContent = JSON.stringify(payload)
    document.head.appendChild(script)
  }
}

export function applySeoHead(input: SeoHeadInput) {
  const url = absoluteUrl(input.path)
  const image = input.ogImage || DEFAULT_OG_IMAGE

  document.title = input.title
  upsertMetaByName('title', input.title)
  upsertMetaByName('description', input.description)
  upsertMetaByName('robots', 'index, follow')

  upsertMetaByProperty('og:type', 'website')
  upsertMetaByProperty('og:url', url)
  upsertMetaByProperty('og:title', input.title)
  upsertMetaByProperty('og:description', input.description)
  upsertMetaByProperty('og:image', image)
  upsertMetaByProperty('og:site_name', SITE_NAME)
  upsertMetaByProperty('og:locale', 'es_CL')

  upsertMetaByName('twitter:card', 'summary_large_image')
  upsertMetaByProperty('twitter:url', url)
  upsertMetaByProperty('twitter:title', input.title)
  upsertMetaByProperty('twitter:description', input.description)
  upsertMetaByProperty('twitter:image', image)

  // Support both name= and property= twitter tags used by different crawlers
  upsertMetaByName('twitter:url', url)
  upsertMetaByName('twitter:title', input.title)
  upsertMetaByName('twitter:description', input.description)
  upsertMetaByName('twitter:image', image)

  upsertCanonical(url)
  upsertJsonLd(input.jsonLd)
}

export function useSeoHead(source: WatchSource<SeoHeadInput | null | undefined>) {
  const stop = watch(
    source,
    (value) => {
      if (value) {
        applySeoHead(value)
      }
    },
    { immediate: true, deep: true },
  )

  onBeforeUnmount(() => {
    stop()
  })
}
